/**
 * The live `/family` container (#577 / R6.2) — the Family Studio on real household data.
 *
 * **What it reads**, each from the endpoint that owns it:
 *
 * | section | endpoint | note |
 * | --- | --- | --- |
 * | Members | `GET /api/admin/users?includeSelf=true` | `admin.routes.js`, admin-only, camelCase |
 * | Removed members | `GET /api/admin/users/removed` | D-33; restore is the only action on one |
 * | Display devices | `GET /api/displays` (E37) | `display.routes.js`, parent-only, refuses a display |
 * | Goals | `GET /api/goals/progress?userId=<id>` | one call per non-parent member |
 * | Household goal | `GET /api/village` | `village: null` means none was ever set |
 * | Pending invitations | `GET /api/auth/signup/invitations` | #577 row 8, never carries a token |
 *
 * **What it writes** (#577 rows 7-10), every one through `api` (`client.js`), which carries the CSRF
 * token and runs the parent re-check (#578): a stale parent window answers 403
 * `PARENT_REAUTH_REQUIRED`, the client prompts once and retries once. A cancelled re-check comes
 * back here as that same 403, and the screen says the change was not made because the check was
 * cancelled — never "you are not a parent".
 *
 *   · members — rename, deactivate, reactivate, restore, family order (`api/family.js`). There is no
 *     role change: the server refuses one by design, so nothing here offers it.
 *   · goals — add, change, delete a child's goal; set or change the household goal.
 *   · displays — pair (E35), rename (E38), unpair (E39).
 *   · invitations — send, resend, revoke, and "Get link": the copy link of ONE pending invitation
 *     (decision d-20260924-01). There is no household join code.
 *
 * **Removed members, the household goal and the pending list are SOFT reads.** If one fails the
 * family still shows, and that part says it could not load rather than claiming "none".
 *
 * **A pairing code is made only when a parent presses the button (#582).** Making one is a POST,
 * and rendering a screen must not mutate a household. The code is shown with the address a
 * display types it into (`#/pair`, `PairRoute.jsx`) and is taken away when it expires.
 *
 * **PIN state is absent on purpose.** Nothing reports whether a member has a PIN, so the member
 * cards carry no PIN dots and no trust-mode note. #576 (R6.1) owns the identity work behind them.
 *
 * **The vanishing-warning trap**, the same one `VillageRoute` and `ApprovalsRoute` document:
 * going live leaves fixture mode, which silently REMOVES `SnapshotNotice`. This route carries its
 * own note instead.
 */
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';

import Button from '../components/Button.jsx';
import LanternLoader from '../components/LanternLoader.jsx';
import { ApiError, PARENT_REAUTH_CODE, api } from '../api/client.js';
import { DISPLAYS_PATH, mintPairCode, renameDisplay, unpairDisplay } from '../api/displays.js';
import {
  INVITATIONS_PATH, REMOVED_MEMBERS_PATH, VILLAGE_PATH,
  createGoal, deactivateMember, deleteGoal, fetchInvitationLink, reactivateMember, renameMember,
  reorderMembers, resendInvitation, restoreMember, revokeInvitation, sendInvitation, householdGoalSaver,
  updateGoal,
} from '../api/family.js';
import {
  inviteLinkLabel, toDevices, toGoalRows, toHouseholdGoal, toMembers, toPendingInvites, toRemovedMembers,
} from '../lib/familyMapping.js';
import { TENANT_PATH } from '../api/session.js';
import { knownCurrency } from '../lib/heldMoney.js';
import { TENANT_READ_TIMEOUT_MS, softReadAfter } from '../lib/softRead.js';
import { useOptionalActor } from '../state/ActorContext.jsx';
import {
  DeviceRenameEditor, GoalEditor, HouseholdGoalEditor, MemberEditor,
} from '../components/FamilyEditors.jsx';
import { PAIR_PATH } from './PairRoute.jsx';

export { PAIR_PATH };

const Family = lazy(() => import('../pages/Family.jsx'));

/** Carried on the page, because leaving fixture mode removes the fixture banner. */
export const NOT_BUILT_NOTICE =
  'Live family data. Whether a member has a PIN is not shown here yet.';

/** Said while the read is in flight or has failed — "Live family data." is false in both. */
export const NOT_LIVE_YET = 'Whether a member has a PIN is not shown here yet.';

export const PENDING_UNREADABLE =
  'Pending invitations could not be loaded just now. Reload the page to see them.';

/**
 * One sentence for a refused or failed write. The server's own words are used when it sent them,
 * because its errors here are written for a person (`admin.service.js`, `goal.routes.js`,
 * `signup.service.js`).
 */
export function writeMessage(error, what = 'That change') {
  if (error instanceof ApiError && error.status === 403) {
    return error.body?.code === PARENT_REAUTH_CODE
      ? `${what} was not made, because the parent check was cancelled.`
      : 'Only a parent can make that change.';
  }
  const said = error instanceof ApiError && typeof error.body?.error === 'string' ? error.body.error.trim() : '';
  if (said) return said;
  return `${what} was not saved — nothing was changed. Try again in a moment.`;
}

/** Settle a read without failing the whole screen. */
const soft = (promise) => promise.then((value) => ({ ok: true, value }), (error) => ({ ok: false, error }));

const MINT_FAILED = 'No usable pairing code came back. Try again in a moment.';
const UNPAIR_UNCONFIRMED =
  'This display may still be connected — the unpair was not confirmed. Press Unpair again.';

/** The address a display opens to type the code, on whatever host serves this app. */
export function pairAddress() {
  return `${window.location.origin}/#${PAIR_PATH}`;
}

function clock(ms) {
  return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export const LOAD_FAILED =
  "We couldn't load your family just now — give it another moment.";

export const NOT_A_PARENT =
  'Family Studio is a parent screen. Your account is a member account, so this page has nothing '
  + 'to show you.';

export const EMPTY_LABEL =
  'No family members yet. Invite an adult by email and they will appear here.';

/**
 * One sentence for a failed read.
 *
 * Both reads behind this screen are `requireAdmin`, so a 403 is not a fault — it is a member
 * account on a parent screen, and saying "give it another moment" would send them round a loop
 * that can never open.
 */
export function loadMessage(error) {
  if (error instanceof ApiError && error.status === 401) {
    return 'Your sign-in has expired — please sign in again.';
  }
  if (error instanceof ApiError && error.status === 403) return NOT_A_PARENT;
  return LOAD_FAILED;
}

/**
 * The household read, in two waves.
 *
 * Wave 1 is the member list and the display tokens. Wave 2 asks for goal progress once per
 * non-parent member, because `GET /api/goals/progress` answers for ONE user and there is no
 * whole-family variant. A household is small, so the call count is the family's size, and a
 * per-child read is the only shape the server offers.
 */
/**
 * Refuse an envelope that did not actually answer the question.
 *
 * Without this, a `{ success: false }` or a `{}` fell through `toMembers` to `[]`, the route
 * entered `ready`, and the page said "No family members yet" — a confident empty claim about a
 * real household, which is the exact defect every `null` in this file exists to prevent. Found by
 * a cross-model reviewer, who was right that the read half had the same hole as the write half.
 */
function requireList(payload, key, what) {
  const ok = payload !== null && typeof payload === 'object'
    && payload.success !== false
    && Array.isArray(payload[key]);
  if (ok) return payload;
  throw Object.assign(new Error(`The ${what} response was not readable`), { statusCode: 502 });
}

async function loadFamily(signal) {
  const required = Promise.all([
    api.get('/api/admin/users?includeSelf=true', { signal }),
    api.get(DISPLAYS_PATH, { signal }),
    soft(api.get(REMOVED_MEMBERS_PATH, { signal })),
    soft(api.get(VILLAGE_PATH, { signal })),
  ]);
  // The per-child goals are the screen's SECOND required round: they need the member list first.
  // The round starts the moment that list answers, beside the family read rather than after it.
  const goals = required.then(([usersPayload, devicesPayload]) => {
    requireList(usersPayload, 'users', 'family member');
    requireList(devicesPayload, 'devices', 'display device');
    const kids = toMembers(usersPayload).filter((m) => m.role !== 'parent');
    return Promise.all(
      kids.map((kid) => api.get(`/api/goals/progress?userId=${encodeURIComponent(kid.id)}`, { signal })),
    );
  });
  const [[usersPayload, devicesPayload, removed, village], lists, tenant] = await Promise.all([
    required,
    goals,
    // #580: the family's currency for money goals. Soft and bounded: a read that fails, or does not
    // answer within TENANT_READ_TIMEOUT_MS of the screen's LAST required round (the goals), withholds
    // the unit only, never the screen. An answer that lands while the screen is still loading is
    // kept, because the screen could not have appeared any sooner (Qwen q3 M1).
    softReadAfter(api.get(TENANT_PATH, { signal }), goals, TENANT_READ_TIMEOUT_MS),
  ]);

  const members = toMembers(usersPayload);
  const kids = members.filter((m) => m.role !== 'parent');

  const progressByUserId = {};
  kids.forEach((kid, i) => { progressByUserId[kid.id] = lists[i]; });

  const removedOk = removed.ok && Array.isArray(removed.value?.users);
  const villageOk = village.ok && village.value && typeof village.value === 'object' && 'village' in village.value;
  return {
    members,
    devices: toDevices(devicesPayload),
    goals: toGoalRows(members, progressByUserId),
    // null = not read. The card only appears for a non-empty list, so "not read" claims nothing.
    removed: removedOk ? toRemovedMembers(removed.value) : null,
    // undefined = not read, which hides the row; null = read, and no goal was ever set.
    householdGoal: villageOk ? toHouseholdGoal(village.value) : undefined,
    currency: knownCurrency(tenant?.tenant?.currency),
  };
}

/** The pending list, strictly: an envelope that did not answer is a failure, not "none". */
async function loadPending(signal) {
  const payload = await api.get(INVITATIONS_PATH, { signal });
  requireList(payload, 'invitations', 'pending invitation');
  return toPendingInvites(payload);
}

export default function FamilyRoute() {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [reloadKey, setReloadKey] = useState(0);
  const [invite, setInvite] = useState({ sending: false, error: '' });

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    // A re-read after a change keeps the page on screen (`refreshing`), so an open card, its
    // announcement and the parent's place are not wiped by a skeleton.
    setState((prev) => ({ ...prev, status: prev.data ? 'refreshing' : 'loading', error: null }));
    loadFamily(controller.signal)
      .then((data) => { if (live) setState({ status: 'ready', data, error: null }); })
      .catch((error) => {
        if (!live || controller.signal.aborted) return;
        setState((prev) => ({ ...prev, status: prev.data ? 'stale' : 'error', error }));
      });
    return () => { live = false; controller.abort(); };
  }, [reloadKey]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);
  // #583 N1 S4: one saver for this screen, so a retry of the same household-goal save reuses its operation key.
  const [saveHouseholdGoal] = useState(householdGoalSaver);

  // #577 row 8 — the pending list, read on its own so a refresh after resend or revoke never
  // re-renders the whole family.
  const [pending, setPending] = useState({ status: 'loading', list: null });
  const [invitesKey, setInvitesKey] = useState(0);
  const reloadInvites = useCallback(() => setInvitesKey((n) => n + 1), []);
  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    loadPending(controller.signal)
      .then((list) => {
        if (!live) return;
        setPending({ status: 'ready', list });
        setShare((prev) => prev && !list.some((item) => String(item.id) === String(prev.id)) ? null : prev);
      })
      .catch(() => { if (live && !controller.signal.aborted) setPending({ status: 'error', list: null }); });
    return () => { live = false; controller.abort(); };
  }, [invitesKey]);
  // The one invitation link on screen, fetched when a parent asks (decision d-20260924-01).
  const [share, setShare] = useState(null);
  const [inviteNote, setInviteNote] = useState(undefined);

  // #577 rows 7, 9, 10 — the one open edit panel.
  const actorContext = useOptionalActor();
  const selfId = actorContext?.actor?.id != null ? String(actorContext.actor.id) : null;
  const [edit, setEdit] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState('');
  const [notice, setNotice] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  const openEdit = useCallback((next) => {
    setEdit(next);
    setEditError('');
    setNotice(null);
  }, []);
  const closeEdit = useCallback(() => { setEdit(null); setEditError(''); }, []);

  /** Run one write from the open panel: close it and re-read on success, keep it open on refusal. */
  const runEdit = useCallback(async (work, done, what) => {
    setEditBusy(true);
    setEditError('');
    try {
      await work();
      setEdit(null);
      setNotice(done);
      reload();
    } catch (error) {
      setEditError(writeMessage(error, what));
      // Keep the editor and its refusal visible, but refresh the revision before retry.
      if (what === 'The household goal' && error instanceof ApiError && error.status === 409) reload();
    } finally {
      setEditBusy(false);
    }
  }, [reload]);

  const restore = useCallback(async (member) => {
    setRestoringId(member.id);
    setNotice(null);
    try {
      await restoreMember(member.id);
      setNotice(`${member.name} is back in the family.`);
      reload();
    } catch (error) {
      setNotice(writeMessage(error, 'The restore'));
    } finally {
      setRestoringId(null);
    }
  }, [reload]);

  // #582 — pairing. `idle` → `minting` → `ready` (a live code) → `expired`, or `failed`.
  const [pair, setPair] = useState({ status: 'idle', code: null, expiresAt: null });
  // Per-display unpair state, by id: `{ busy }` while the DELETE runs, `{ note }` when it failed.
  const [unpairs, setUnpairs] = useState({});

  const makeCode = useCallback(async () => {
    setPair((prev) => ({ ...prev, status: prev.status === 'ready' ? 'refreshing' : 'minting' }));
    try {
      const { code, expiresAt } = await mintPairCode();
      setPair({ status: 'ready', code, expiresAt });
    } catch {
      setPair({ status: 'failed', code: null, expiresAt: null });
    }
  }, []);

  // Take the code off the screen the moment the server stops accepting it.
  useEffect(() => {
    // `refreshing` too: the old code stays on screen while "New code" is in flight, and it must
    // still leave at its own expiry if that request is slow.
    if (pair.status !== 'ready' && pair.status !== 'refreshing') return undefined;
    const timer = setTimeout(() => {
      setPair((prev) => (prev.code === pair.code ? { status: 'expired', code: null, expiresAt: prev.expiresAt } : prev));
    }, Math.max(0, pair.expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [pair.status, pair.code, pair.expiresAt]);

  const unpair = useCallback(async (device) => {
    const id = device?.id;
    if (id == null) return;
    setUnpairs((prev) => ({ ...prev, [id]: { busy: true } }));
    try {
      await unpairDisplay(id);
      setUnpairs((prev) => { const next = { ...prev }; delete next[id]; return next; });
      reload();
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        // Not in this household any more. The fresh list says so better than a note would.
        setUnpairs((prev) => { const next = { ...prev }; delete next[id]; return next; });
        reload();
        return;
      }
      // NOT re-read. E39 answers 500 when the display was revoked but realtime could not be told to
      // drop it, and a fresh list would hide the revoked row, and this warning with it, while the
      // screen may still be showing the family. E39 is idempotent, so a second press is safe.
      // A cancelled parent re-check is ALSO a 403 (`client.js` rethrows the first refusal), and
      // it is not "you are not a parent". The server refused before revoking anything.
      let note = UNPAIR_UNCONFIRMED;
      if (error instanceof ApiError && error.status === 403) {
        note = error.body?.code === PARENT_REAUTH_CODE
          ? 'The display was not unpaired, because the parent check was cancelled.'
          : 'Only a parent can unpair a display.';
      }
      setUnpairs((prev) => ({ ...prev, [id]: { note } }));
    }
  }, [reload]);

  /**
   * The send-invite write.
   *
   * `sendInvitationSchema` requires `email`, `name` AND `role`, and this card collects only an
   * email. So:
   *   · `name` is the email itself. The invitation email greets "Hi," and never uses this field
   *     (`emailService.js`), and `user_invitations.name` visibly holding an address reads as "not
   *     known yet" rather than as a wrong name. Inventing one from the local part would be a guess
   *     about a real person.
   *   · `role` is `'member'`, the least privilege of the service's three valid roles and its own
   *     default. This card has no role picker, so granting admin from it would hand out parent
   *     authority through a plain email box.
   *
   * The RECEIPT is the server's invitation id, and only when the email actually went. The first
   * version returned `{ email }` alone, which `persistWithReceipt` rejects, so a real send was
   * reported as "We couldn't confirm the invitation" (#577 row 8). An invitation saved without its
   * email is said plainly, because the row now exists and Resend or Get link can still deliver it.
   */
  const sendInvite = useCallback(async (email) => {
    const trimmed = typeof email === 'string' ? email.trim() : '';
    if (!trimmed) return null;
    setInvite({ sending: true, error: '' });
    let result;
    try {
      result = await sendInvitation({ email: trimmed, name: trimmed, role: 'member' });
    } catch (error) {
      setInvite({ sending: false, error: writeMessage(error, 'The invitation') });
      throw error;
    }
    reloadInvites();
    const id = result?.invitation?.id;
    if (result?.emailSent !== true || id == null) {
      const message = id == null
        ? 'That invitation did not send — nothing was changed. Try again in a moment.'
        : 'The invitation was saved, but the email did not go out. Use Resend or Get link on it below.';
      setInvite({ sending: false, error: message });
      throw new Error(message);
    }
    setInvite({ sending: false, error: '' });
    return { receiptId: `invitation:${id}`, status: 'sent', email: trimmed };
  }, [reloadInvites]);

  const resend = useCallback(async (item) => {
    setInviteNote(undefined);
    let result;
    try {
      result = await resendInvitation(item.id);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) reloadInvites();
      setInviteNote(writeMessage(error, 'The resend'));
      throw error;
    }
    // The token rotated either way, so a link on screen for this invitation no longer works.
    setShare((prev) => (prev && String(prev.id) === String(item.id) ? null : prev));
    reloadInvites();
    if (result?.emailSent !== true) {
      const message = `The invitation to ${item.email} was not resent: the email did not go out. Try again in a minute, or use Get link.`;
      setInviteNote(message);
      throw new Error(message);
    }
    return { receiptId: `resend:${item.id}:${result.invitation?.sendCount ?? ''}`, status: 'sent', email: item.email };
  }, [reloadInvites]);

  const revoke = useCallback(async (item) => {
    setInviteNote(undefined);
    try {
      await revokeInvitation(item.id);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) reloadInvites();
      setInviteNote(writeMessage(error, 'The revoke'));
      throw error;
    }
    setShare((prev) => (prev && String(prev.id) === String(item.id) ? null : prev));
    reloadInvites();
    return { receiptId: `revoke:${item.id}`, status: 'revoked', email: item.email };
  }, [reloadInvites]);

  const getLink = useCallback(async (item) => {
    setInviteNote(undefined);
    try {
      const answer = await fetchInvitationLink(item.id);
      const inv = answer?.invitation;
      if (typeof inv?.link !== 'string' || !/^https?:\/\//i.test(inv.link)) throw new Error('No usable link came back');
      setShare({ id: item.id, link: inv.link, label: inviteLinkLabel(inv) });
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) reloadInvites();
      setInviteNote(writeMessage(error, 'The link'));
      throw error;
    }
  }, [reloadInvites]);

  const ready = state.status === 'ready' || state.status === 'refreshing' || state.status === 'stale';
  const data = state.data;
  const loadError = state.status === 'error' ? loadMessage(state.error) : null;
  const note = ready ? NOT_BUILT_NOTICE : NOT_LIVE_YET;
  const members = useMemo(() => (data ? data.members : []), [data]);
  const devices = useMemo(
    () => (data ? data.devices : []).map((d) => {
      const u = unpairs[d.id];
      return u ? { ...d, unpairing: u.busy === true, note: u.note ?? d.note } : d;
    }),
    [data, unpairs],
  );

  // "+ Add goal" offers only children who can sign in; a deactivated child keeps their goal row.
  const kids = useMemo(() => members.filter((m) => m.role !== 'parent' && m.isActive), [members]);
  const activeIds = useMemo(() => members.filter((m) => m.isActive).map((m) => m.id), [members]);
  const goals = useMemo(
    () => (data ? data.goals : []).map((g) => ({
      ...g,
      onEdit: () => openEdit({ kind: 'goal', row: g }),
      onAdd: () => openEdit({ kind: 'goal', row: g }),
    })),
    [data, openEdit],
  );

  let editor = null;
  if (edit?.kind === 'member') {
    const m = edit.member;
    const at = activeIds.indexOf(m.id);
    const move = (dir) => {
      const order = [...activeIds];
      const to = dir === 'earlier' ? at - 1 : at + 1;
      [order[at], order[to]] = [order[to], order[at]];
      runEdit(() => reorderMembers(order), `${m.name} moved ${dir} in the family order.`, 'The new order');
    };
    editor = (
      <MemberEditor
        key={`member:${m.id}`}
        member={m}
        isSelf={selfId !== null && selfId === String(m.id)}
        canMoveEarlier={at > 0}
        canMoveLater={at >= 0 && at < activeIds.length - 1}
        busy={editBusy}
        error={editError}
        onSave={(name) => runEdit(() => renameMember(m.id, name), `Saved: ${name}.`, 'The new name')}
        onDeactivate={() => runEdit(() => deactivateMember(m.id), `${m.name} is deactivated and cannot sign in.`, 'Deactivating')}
        onReactivate={() => runEdit(() => reactivateMember(m.id), `${m.name} can sign in again.`, 'Reactivating')}
        onMove={move}
        onCancel={closeEdit}
      />
    );
  } else if (edit?.kind === 'goal') {
    const row = edit.row;
    editor = (
      <GoalEditor
        key={`goal:${row ? row.id : 'new'}`}
        row={row}
        kids={kids}
        busy={editBusy}
        error={editError}
        onSave={({ userId, goalType, target }) => runEdit(
          () => (row?.hasGoal ? updateGoal(row.goalId, { userId: row.id, target }) : createGoal({ userId, goalType, target })),
          'Goal saved.',
          'The goal',
        )}
        onDelete={() => runEdit(() => deleteGoal(row.goalId, row.id), 'Goal deleted.', 'Deleting the goal')}
        onCancel={closeEdit}
      />
    );
  } else if (edit?.kind === 'household') {
    editor = (
      <HouseholdGoalEditor
        key="household"
        goal={data?.householdGoal ?? null}
        busy={editBusy}
        error={editError}
        onSave={(goal) => runEdit(
          () => saveHouseholdGoal({ ...goal, revision: data?.householdGoal?.revision }),
          `The household goal is now ${goal.name}.`,
          'The household goal',
        )}
        onCancel={closeEdit}
      />
    );
  } else if (edit?.kind === 'device') {
    const d = edit.device;
    editor = (
      <DeviceRenameEditor
        key={`device:${d.id}`}
        device={d}
        busy={editBusy}
        error={editError}
        onSave={(name) => runEdit(() => renameDisplay(d.id, name), `The display is now called ${name}.`, 'The new name')}
        onCancel={closeEdit}
      />
    );
  }

  const liveCode = pair.status === 'ready' || pair.status === 'refreshing';
  const makeButton = (
    <Button variant="primary" size="sm" onClick={makeCode} loading={pair.status === 'minting'}>
      Make a pairing code
    </Button>
  );
  let pairInstructions;
  let pairNote;
  if (!liveCode && pair.status !== 'failed') {
    pairInstructions = pair.status === 'expired'
      ? `That code expired at ${clock(pair.expiresAt)}. Make a new one when the display is ready.`
      : `Make a code here, then type it on the new display at ${pairAddress()}.`;
    pairNote = makeButton;
  }

  return (
    <Suspense fallback={<LanternLoader label="Loading Family Studio…" />}>
      <Family
        showSnapshotNotice={false}
        members={members}
        devices={devices}
        onUnpairDevice={unpair}
        onRenameDevice={(device) => openEdit({ kind: 'device', device })}
        onEditMember={(member) => openEdit({ kind: 'member', member })}
        removedMembers={data ? data.removed : null}
        onRestoreMember={restore}
        restoringId={restoringId}
        householdGoal={data ? data.householdGoal : undefined}
        onEditHouseholdGoal={() => openEdit({ kind: 'household' })}
        goals={goals}
        onAddGoal={() => openEdit({ kind: 'goal', row: null })}
        goalsNote={note}
        // `null` until read or when unknown, so a money goal never shows an invented unit.
        currency={data ? data.currency : null}
        editor={editor}
        notice={notice}
        refreshError={state.status === 'stale' ? 'Could not refresh the family. Your completed change is saved; the details below may be out of date.' : null}
        // The link of ONE pending invitation, fetched on "Get link", or `null` — never `''`, which
        // would still render a copy control for a link that does not exist.
        shareLink={share ? share.link : null}
        shareLabel={share ? share.label : undefined}
        // `null` until the list is read, NOT `[]`, which would claim it was read and was empty.
        invitePending={pending.list}
        onResendInvite={resend}
        onRevokeInvite={revoke}
        onGetInviteLink={getLink}
        inviteNote={pending.status === 'error' ? PENDING_UNREADABLE : inviteNote}
        pairCode={liveCode ? pair.code : null}
        pairDisplayUrl={liveCode ? pairAddress() : null}
        // #582 slice 5: `#/display` is now the LIVE board, which answers a parent's browser with "this
        // is signed in as a person". A preview is the fixture board, so the link names it.
        displayPreviewHref="#/gallery/Display"
        pairSteps={liveCode ? [
          `This code works once, until ${clock(pair.expiresAt)}.`,
          'When the display says it is paired, reload this page to see it in the list.',
        ] : null}
        onRefreshPairCode={liveCode ? makeCode : undefined}
        pairRefreshing={pair.status === 'refreshing'}
        pairInstructions={pairInstructions}
        pairNote={pairNote}
        pairError={pair.status === 'failed' ? MINT_FAILED : undefined}
        onRetryPairCode={pair.status === 'failed' ? makeCode : undefined}
        onSendInvite={sendInvite}
        inviteSending={invite.sending}
        inviteEmailError={invite.error}
        emptyLabel={EMPTY_LABEL}
        loading={state.status === 'loading'}
        error={loadError}
        onRetry={reload}
      />
    </Suspense>
  );
}
